import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { NbToastrService } from '@nebular/theme';
import {
  BuildingEnergyCertificateDetailDto,
  BuildingEnergyCertificateMergeDto,
  BuildingEnergyCertificateService,
  BuildingService,
  EnergyCertificateEntryDto,
  EnergyCertificateResultDto,
  EnergyClassThresholdDto,
  SensorItemDto,
} from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { loader } from '@/utils/signals/loader';

@Component({
  standalone: false,
  selector: 'wr-building-energy-pass',
  templateUrl: './building-energy-pass.component.html',
  styleUrl: './building-energy-pass.component.scss',
})
export class BuildingEnergyPassComponent {
  readonly buildingId = input.required<string>();
  readonly editMode = input(false);

  readonly sensorPurposes = [SensorItemDto.PurposeEnum.RootMeter, SensorItemDto.PurposeEnum.UnitMeter];
  readonly forecastFieldHint =
    'Ersetzt den Messwert dieses Sensors im Energieausweis, bis der Sensor Messdaten für ein volles Jahr (12 Monate) liefert.';

  private readonly certificateService = inject(BuildingEnergyCertificateService);
  private readonly buildingService = inject(BuildingService);
  private readonly toastrService = inject(NbToastrService);
  private readonly formBuilder = inject(FormBuilder);

  readonly loading = loader();
  readonly saving = signal(false);
  private readonly reload = signal(0);

  readonly certificate = asyncComputed(() => {
    this.reload();
    const buildingId = this.buildingId();
    if (!buildingId) return undefined;
    return this.certificateService.findByBuilding(buildingId);
  }, undefined, this.loading);

  readonly building = asyncComputed(() => {
    const buildingId = this.buildingId();
    if (!buildingId) return undefined;
    return this.buildingService.findById(buildingId);
  }, undefined);

  readonly form = signal<FormGroup | undefined>(undefined);
  readonly entriesArray = signal<FormArray | undefined>(undefined);
  readonly latest = signal<EnergyCertificateResultDto | null>(null);
  readonly energyClasses = signal<EnergyClassThresholdDto[]>([]);
  readonly certifiedEnergyDemand = signal<number | null>(null);

  readonly hasCertificate = computed(() => !!this.certificate());

  readonly warnings = computed(() =>
    (this.latest()?.breakdown ?? []).filter(c => c.warningReason !== 'NONE'),
  );

  readonly warningMessage = computed(() => {
    const labels = this.warnings().map(c => c.label || c.sensorName || (c.type === 'SENSOR' ? 'Sensor' : 'Absolutwert'));
    return `Einträge ohne verwertbare Daten: ${labels.join(', ')}`;
  });

  constructor() {
    effect(() => {
      const certificate = this.certificate();
      const building = this.building();
      if (certificate === undefined || building === undefined) return;
      this.buildForm(certificate, building.baseData?.area ?? null);
      this.latest.set(certificate?.latest ?? null);
      this.energyClasses.set(certificate?.energyClasses ?? []);
      this.certifiedEnergyDemand.set(certificate?.certifiedEnergyDemand ?? null);
    });

    effect(() => {
      const form = this.form();
      const editMode = this.editMode();
      if (!form) return;
      if (editMode) {
        form.enable({ emitEvent: false });
      } else {
        form.disable({ emitEvent: false });
      }
    });
  }

  private buildForm(certificate: BuildingEnergyCertificateDetailDto | null, buildingArea: number | null): void {
    const entries = new FormArray(
      (certificate?.entries ?? []).map(entry => this.createEntryGroup(entry)),
    );
    const form = this.formBuilder.group({
      livingArea: new FormControl<number | null>(certificate?.livingArea ?? buildingArea, [
        Validators.required,
        Validators.min(0.01),
      ]),
      heatingSystemType: new FormControl<string>(certificate?.heatingSystemType ?? 'FOSSIL'),
      certifiedEnergyDemand: new FormControl<number | null>(certificate?.certifiedEnergyDemand ?? null, [
        Validators.min(0),
      ]),
      entries,
    });
    this.entriesArray.set(entries);
    this.form.set(form);
  }

  private createEntryGroup(entry?: EnergyCertificateEntryDto): FormGroup {
    return new FormGroup({
      id: new FormControl<string | null>(entry?.id ?? null),
      operator: new FormControl<string>(entry?.operator ?? 'PLUS'),
      type: new FormControl<string>(entry?.type ?? 'ABSOLUTE'),
      sensor: new FormControl<SensorItemDto | undefined>(
        entry?.sensorId
          ? ({
            id: entry.sensorId,
            name: entry.sensorName,
            sensorBlockName: entry.sensorBlockName,
            color: entry.sensorColor,
          } as SensorItemDto)
          : undefined,
      ),
      absoluteValue: new FormControl<number | null>(entry?.absoluteValue ?? null),
      forecastValue: new FormControl<number | null>(entry?.forecastValue ?? null),
      label: new FormControl<string | null>(entry?.label ?? null),
      markedForDeletion: new FormControl<boolean>(false),
    });
  }

  visibleEntries(): FormGroup[] {
    const controls = (this.entriesArray()?.controls ?? []) as FormGroup[];
    return controls.filter(control => !control.get('markedForDeletion')?.value);
  }

  hasPendingChanges(): boolean {
    return this.form()?.dirty ?? false;
  }

  addEntry(): void {
    this.entriesArray()?.push(this.createEntryGroup());
    this.form()?.markAsDirty();
  }

  removeEntry(group: FormGroup): void {
    const array = this.entriesArray();
    if (!array) return;
    if (group.get('id')?.value) {
      group.get('markedForDeletion')?.setValue(true);
    } else {
      array.removeAt(array.controls.indexOf(group));
    }
    this.form()?.markAsDirty();
  }

  toggleOperator(group: FormGroup): void {
    const control = group.get('operator');
    control?.setValue(control.value === 'PLUS' ? 'MINUS' : 'PLUS');
    this.form()?.markAsDirty();
  }

  cancel(): void {
    const certificate = this.certificate();
    if (certificate !== undefined) {
      this.buildForm(certificate, this.building()?.baseData?.area ?? null);
    }
  }

  async save(): Promise<boolean> {
    const form = this.form();
    if (!form) return false;
    form.markAllAsTouched();
    if (form.invalid) {
      this.toastrService.danger('Bitte eine gültige Brutto-Grundfläche (> 0) angeben.', 'Fehler');
      return false;
    }

    this.saving.set(true);
    try {
      const raw = form.getRawValue() as {
        livingArea: number;
        heatingSystemType: BuildingEnergyCertificateMergeDto.HeatingSystemTypeEnum;
        certifiedEnergyDemand: number | null;
        entries: any[];
      };
      const dto: BuildingEnergyCertificateMergeDto = {
        id: this.certificate()?.id ?? undefined,
        buildingId: this.buildingId(),
        livingArea: raw.livingArea,
        heatingSystemType: raw.heatingSystemType,
        certifiedEnergyDemand: raw.certifiedEnergyDemand ?? undefined,
        entries: raw.entries.map(entry => ({
          id: entry.id ?? undefined,
          operator: entry.operator,
          type: entry.type,
          sensorId: entry.type === 'SENSOR' ? (entry.sensor?.id ?? undefined) : undefined,
          absoluteValue: entry.type === 'ABSOLUTE' ? (entry.absoluteValue ?? undefined) : undefined,
          forecastValue: entry.type === 'SENSOR' ? (entry.forecastValue ?? undefined) : undefined,
          label: entry.label ?? undefined,
          markedForDeletion: entry.markedForDeletion ?? false,
        })),
      };
      const result = await firstValueFrom(this.certificateService.merge(dto));
      this.latest.set(result.latest ?? null);
      this.energyClasses.set(result.energyClasses ?? []);
      this.certifiedEnergyDemand.set(result.certifiedEnergyDemand ?? null);
      this.reload.update(value => value + 1);
      this.toastrService.success('Energieausweis gespeichert', 'Erfolg');
      return true;
    } catch (error) {
      console.error('Energy certificate save failed:', error);
      this.toastrService.danger('Speichern fehlgeschlagen.', 'Fehler');
      return false;
    } finally {
      this.saving.set(false);
    }
  }
}
